import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { InlineAlert } from '@rbilabs/components-library';
import { Icon } from '@rbilabs/components-library/build/components/icon';
import { Tab, TabList } from '@rbilabs/components-library/build/components/tabs';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IOffer } from '@rbi-ctg/menu';
import { Tile } from 'components/features/components/marketing-tile-group/marketing-tile-group';
import LoadingAnimation from 'components/loading-animation';
import { HorizontalPosition, TileSize } from 'components/marketing-tile';
import { OfferCell } from 'components/offer/ui-refresh/cell';
import { useEnablePullToReloadOnScroll } from 'components/pull-to-reload';
import { useFavoriteOffers } from 'hooks/favorite-offers';
import { useOffersPageMarketingTiles } from 'hooks/use-combine-offers-and-marketing-tiles';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { FavoritesEmptyState } from 'pages/loyalty/loyalty-offers/favorite-empty-state';
import { LoyaltyOffersCooldown } from 'pages/loyalty/loyalty-offers/loyalty-offers-cooldown';
import { OffersEmptyState } from 'pages/loyalty/loyalty-offers/offers-empty-state';
import { AvailableTabs } from 'pages/loyalty/loyalty-offers/types';
import { LoyaltyPullToReload } from 'pages/loyalty/loyalty-pull-to-reload';
import { useAuthContext } from 'state/auth';
import { useGeolocation } from 'state/geolocation';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useNearbyStoresContext } from 'state/nearby-stores';
import { useOffersContext } from 'state/offers';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { primitive } from 'styles/constants/primitives';
import { getUniqIdForOffer, sortOffersForSelectedServiceMode } from 'utils/offers';
import {
  PerformancePages,
  initializePagePerformance,
  pageLoaded,
  pageUnloaded,
  performanceReport,
} from 'utils/render-performance';

import { LOADING_ANIMATION_AS_REFRESH_CONTENT } from './constants';
import { EndOfOffers } from './end-of-offers';
import { OfferSelected } from './offer-selected';
import { useOffersUIContext } from './offers-ui-context';
import { SelectRestaurantCta } from './select-restaurant-cta';

const LoadingAnimationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 3rem;
`;

const ContentContainer = styled.div`
  height: 100%;
  overflow-y: scroll;
  ${Styles.desktop} {
    position: relative;
    width: 100%;
  }
`;

const List = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

export const FavoriteTabs = styled.div`
  padding-block-end: 1rem;
  background: ${p => p.theme.token('background-default')};
`;

export const TabDisclaimer = styled.div`
  display: flex;
  padding: 1rem;
  background: ${primitive.$blackOpacity4};
  font-size: 0.8rem;
  line-height: 1rem;
  gap: 0.6rem;
  margin-block-start: -1rem;
`;

// Track page loading performance
initializePagePerformance(PerformancePages.OFFERS_PAGE);

export const BrowsingPanel: React.FC = () => {
  const { formatMessage } = useIntl();
  const enableSortOffersForServiceMode = useFlag(
    LaunchDarklyFlag.ENABLE_SORT_OFFERS_FOR_SERVICE_MODE
  );
  const hideOffersForServiceMode = useFlag(LaunchDarklyFlag.ENABLE_HIDE_OFFERS_FOR_SERVICE_MODE);
  const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);

  const loyaltyOffersInCooldown = useAppSelector(selectors.loyalty.selectLoyaltyOffersInCooldown);
  const { noStoreSelected, restaurantLogAttributesRef } = useStoreContext();
  const { isAuthenticated } = useAuthContext();
  const { selectedOffer, clearSelectedOffer } = useOffersContext();
  const { toggleFavorite, isFavorite, isOfferLoading } = useFavoriteOffers();
  const { address1: addressOfNearestStore, restaurantGroup } = useNearbyStoresContext();
  const mParticle = useMParticleContext();

  const [selectedTab, setSelectedTab] = useState<AvailableTabs>(AvailableTabs.AllOffers);
  const {
    featureOffers,
    loading,
    showEmptyState,
    offers,
    offersFeedback = {},
    onSelectOffer,
    previewOfferId,
    refreshOffers,
  } = useOffersUIContext();
  const { serviceMode } = useOrderContext();

  const { getScrollPositionForReload, pullToReloadEnabled, setScrollRef } =
    useEnablePullToReloadOnScroll();
  const { isPermissionGranted } = useGeolocation();

  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const enableSelectRestaurantCta = useFlag(LaunchDarklyFlag.ENABLE_OFFERS_SELECT_RESTAURANT_CTA);
  const enableGeolocation = useFlag(LaunchDarklyFlag.ENABLE_SETTINGS_GEOLOCATION);
  const { offersBrowsingPanel = null, offersPageMarketingTiles = [] } = featureOffers || {};

  const filterSelectedOffer = (offer: IOffer) =>
    getUniqIdForOffer({ id: offer._id, token: offer.tokenId }) !== selectedOfferUniqueId;

  const minOffers = offersBrowsingPanel?.offersSelectRestaurantCta?.minimumNumberOfOffers ?? null;
  const endOfOffersImage = offersBrowsingPanel?.endOfOffersImage ?? null;
  const endOfOffersMessage = offersBrowsingPanel?.endOfOffersMessage ?? null;

  const selectedOfferUniqueId =
    selectedOffer &&
    getUniqIdForOffer({
      id: selectedOffer._id,
      token: selectedOffer.tokenId,
    });

  const handleTabChange = useCallback(
    (event: React.ChangeEvent, value: number) => {
      event.preventDefault();
      mParticle.logNavigationClick(CustomEventNames.BUTTON_CLICK, {
        name: value ? 'Favorites' : 'All Offers',
        ...restaurantLogAttributesRef.current,
      });
      setSelectedTab(value);
    },
    [mParticle]
  );

  useEffect(() => {
    initializePagePerformance(PerformancePages.OFFERS_PAGE);

    // If offers are still loading do nothing
    if (loading) {
      return;
    }

    requestAnimationFrame(() => {
      // Page content loaded event
      pageLoaded(PerformancePages.OFFERS_PAGE);

      if (loading) {
        return;
      }

      mParticle.trackEvent({
        name: CustomEventNames.LOYALTY_LOAD,
        type: EventTypes.Other,
        attributes: performanceReport(PerformancePages.OFFERS_PAGE),
      });
    });

    return () => {
      pageUnloaded(PerformancePages.OFFERS_PAGE);
    };
  }, [loading]);

  const offerUpdatedMessage = useMemo(() => {
    return restaurantGroup?.name
      ? formatMessage(
          { id: 'offersUpdatedByLocation' },
          {
            location: restaurantGroup?.name,
          }
        )
      : formatMessage({ id: 'offersUpdated' });
  }, [formatMessage, restaurantGroup]);

  const shouldRenderOfferUpdatedMessage = useMemo(
    () => enableGeolocation && noStoreSelected && (restaurantGroup?.name || addressOfNearestStore),
    [addressOfNearestStore, enableGeolocation, noStoreSelected, restaurantGroup]
  );

  // sort offers by SM based on LD flag & if user has selected a SM
  const shouldSortOffersForServiceMode =
    (enableSortOffersForServiceMode || hideOffersForServiceMode) && !!serviceMode;
  const offersToDisplay = shouldSortOffersForServiceMode
    ? sortOffersForSelectedServiceMode(offers, serviceMode, hideOffersForServiceMode)
    : offers;

  // True when offers with no restaurant selection is less than min offers specified in sanity.
  // will hide offers and show CTA card prompting restaurant selection to unlock restaurant restricted offers.
  const availableOffersLessThanMinimum =
    enableSelectRestaurantCta && noStoreSelected && minOffers && offers.length < minOffers;

  const loadingNearbyStore = useMemo(
    () =>
      enableGeolocation
        ? isPermissionGranted && !addressOfNearestStore && addressOfNearestStore !== null
        : false,
    [addressOfNearestStore, enableGeolocation, isPermissionGranted]
  );

  const { sortedTilesAndOffers, marketingTileTop, marketingTileBottom } =
    useOffersPageMarketingTiles({
      offers: offersToDisplay,
      offersPageMarketingTiles,
      selectedOffer,
    });

  const offersAndTiles = sortedTilesAndOffers
    ?.filter(filterSelectedOffer)
    .map((offerOrTile: any, index: number) => {
      const isOffer = offerOrTile?._type === 'offer';

      // If we selected the Favorites tab we filter any non-favorited offer / tile
      if (
        enableFavoriteOffers &&
        selectedTab === AvailableTabs.Favorites &&
        !isFavorite(offerOrTile._id || '')
      ) {
        return null;
      }

      if (isOffer) {
        return (
          <OfferCell
            key={`${offerOrTile._id}-${index}`}
            offer={offerOrTile}
            offerFeedback={offersFeedback[offerOrTile._id]}
            onClick={onSelectOffer}
            isSelected={!isMobile && previewOfferId === offerOrTile._id}
            favorited={isFavorite(offerOrTile._id)}
            favoriteEnabled={enableFavoriteOffers && isAuthenticated()}
            favoriteLoading={isOfferLoading(offerOrTile._id)}
            handleToggleFavorite={toggleFavorite}
          />
        );
      }
      return (
        <Tile
          tile={offerOrTile}
          key={offerOrTile?._id}
          size={TileSize.HALF}
          buttonPosition={HorizontalPosition.RIGHT}
          hasBorder={false}
          additionalPadding={`0.5rem`}
        />
      );
    })
    .filter((item: any) => !!item);

  const shouldHideSelectedOffer =
    selectedTab === AvailableTabs.Favorites && !isFavorite(selectedOffer?._id || '');

  if (loading && loadingNearbyStore) {
    return (
      <LoadingAnimationContainer data-testid="browsing-panel-loading-animation">
        <LoadingAnimation fillColor={Styles.color.primary} />
      </LoadingAnimationContainer>
    );
  }

  if (showEmptyState) {
    return (
      <ContentContainer data-testid="browsing-panel-empty-state">
        <OffersEmptyState />
      </ContentContainer>
    );
  }

  const favoritesEmptyState = !sortedTilesAndOffers?.some(offer => isFavorite(offer._id));

  return (
    <ContentContainer
      data-testid="browsing-panel"
      onScroll={getScrollPositionForReload}
      ref={setScrollRef}
    >
      <LoyaltyPullToReload
        onRefresh={refreshOffers}
        enabled={pullToReloadEnabled}
        refreshContent={
          LOADING_ANIMATION_AS_REFRESH_CONTENT ? (
            <LoadingAnimationContainer>
              <LoadingAnimation fillColor={Styles.color.primary} />
            </LoadingAnimationContainer>
          ) : undefined
        }
      >
        {enableFavoriteOffers && isAuthenticated() && (
          <FavoriteTabs data-testid="favorite-tabs">
            <TabList selectedIndex={selectedTab} onChange={handleTabChange}>
              <Tab
                aria-controls="browsing-panel-all"
                id="panel-all"
                label={formatMessage({ id: 'offersAll' })}
                selected={selectedTab === AvailableTabs.AllOffers}
              />
              <Tab
                aria-controls="browsing-panel-favorites"
                id="panel-favorites"
                data-testid="panel-favorites"
                label={formatMessage({ id: 'offersFavorites' })}
                selected={selectedTab === AvailableTabs.Favorites}
              />
            </TabList>
          </FavoriteTabs>
        )}
        {enableFavoriteOffers && selectedTab === AvailableTabs.Favorites && (
          <TabDisclaimer>
            <Icon icon={'info'} color={'tertiary'} width="22px" aria-hidden />
            {formatMessage({ id: 'offersFavoritesDisclaimer' })}
          </TabDisclaimer>
        )}
        {loyaltyOffersInCooldown && <LoyaltyOffersCooldown />}
        <List>
          {shouldRenderOfferUpdatedMessage && !loadingNearbyStore && (
            <InlineAlert message={offerUpdatedMessage} icon="checkCircle" />
          )}
          {marketingTileTop && selectedTab !== AvailableTabs.Favorites && (
            <Tile
              data-testid="top-marketing-tile"
              tile={marketingTileTop}
              size={TileSize.HALF}
              buttonPosition={HorizontalPosition.RIGHT}
              hasBorder={false}
              additionalPadding={`0.5rem`}
            />
          )}
          {selectedOffer && !shouldHideSelectedOffer && (
            <OfferSelected
              data-testid="selected-offer"
              offer={selectedOffer}
              onRemoveSelectedOffer={() => {
                clearSelectedOffer({ doLog: true, popToast: true });
              }}
              onClick={onSelectOffer}
              isSelected={!isMobile && previewOfferId === selectedOffer._id}
              favorited={isFavorite(selectedOffer._id)}
              favoriteEnabled={enableFavoriteOffers}
              handleToggleFavorite={toggleFavorite}
            />
          )}
          {!availableOffersLessThanMinimum && !!offersAndTiles?.length && offersAndTiles}
          {enableFavoriteOffers && favoritesEmptyState && shouldHideSelectedOffer && (
            <FavoritesEmptyState data-testid="favorite-empty-state" />
          )}
          {marketingTileBottom && selectedTab !== AvailableTabs.Favorites && (
            <Tile
              data-testid="bottom-marketing-tile"
              tile={marketingTileBottom}
              size={TileSize.HALF}
              buttonPosition={HorizontalPosition.RIGHT}
              hasBorder={false}
              additionalPadding={`0.5rem`}
            />
          )}
          {enableSelectRestaurantCta && <SelectRestaurantCta />}
          {endOfOffersMessage && (
            <EndOfOffers message={endOfOffersMessage} image={endOfOffersImage} />
          )}
        </List>
      </LoyaltyPullToReload>
    </ContentContainer>
  );
};
